import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import * as PR from "../../../prime-react";
import Layout from "../../../layouts/Layout";
import { getUserAuditLogsnewAPI, getUsersnewAPI } from "../../../services/api";
import { useToast } from "../../../context/ToastContext";
import { areDateRangesEqual, dateTimeFormat, defaultDateRange, pagecount } from "../../../utils/reuse";
import "./AuditLog.scss";
import moment from "moment";
import { normalizeAuditLogs } from "./auditLogTransform";

const actionFilterOptions = [
    { label: "Login", value: { type: "login" } },
    { label: "Chat", value: { type: "chat" } },
    { label: "Alert export", value: { type: "alert" } },
    { label: "Alert download", value: { type: "download", download_type: "alert" } },
    { label: "News download", value: { type: "download", download_type: "news" } },
    { label: "Report download", value: { type: "download", download_type: "report" } },
    { label: "Response download", value: { type: "download", download_type: "response" } },
];

const AuditLog = () => {
    const { showToast } = useToast();
    
    const sessionData = useSelector((state) => state.auth.sessionData);
    const headers = useMemo(() => ({ sessionid: sessionData.sessionId }), [sessionData.sessionId]);
    const [loading, setLoading] = useState(false);
    const [userOptionsLoading, setUserOptionsLoading] = useState(false);
    const [auditLogs, setAuditLogs] = useState([]);
    const [userOptions, setUserOptions] = useState([]);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [selectedAction, setSelectedAction] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [dates, setDates] = useState(defaultDateRange);
    const [sortOrder, setSortOrder] = useState(-1);
    const pageCount = pagecount;
    const isDefaultDateRange = areDateRangesEqual(dates, defaultDateRange);

    const fetchAuditLogs = useCallback(async (page = 1) => {
        const params = {
            offset: (page - 1) * pageCount,
            limit: pageCount + 1,
            sort_by: "created_at",
            sort_order: sortOrder === 1 ? "asc" : "desc",
        };

        if (selectedUsers?.length) {
            params.users = selectedUsers.join(',');
        }

        if (selectedAction) {
            Object.assign(params, selectedAction);
        }

        if (dates && dates?.length > 0) {
            const startDate = moment(dates[0]).format("YYYY-MM-DD");
            const endDate = dates[1] ? moment(dates[1]).format("YYYY-MM-DD") : startDate;
            params.startDate = startDate;
            params.endDate = endDate;
        }

        setLoading(true);
        const { result, data, error } = await getUserAuditLogsnewAPI(params, headers);
        if (result === "SUCCESS") {
            setAuditLogs(normalizeAuditLogs(data));
        } else {
            setAuditLogs([]);
            showToast(error?.severity, error?.errorMsg || error?.summary);
        }
        setLoading(false);
    }, [pageCount, sortOrder, selectedUsers, selectedAction, dates, headers, showToast]);

    const fetchUsers = useCallback(async () => {
        setUserOptionsLoading(true);
        const { result, data, error } = await getUsersnewAPI(headers);
        if (result === "SUCCESS") {
            const options = (data || [])
                .map((user) => ({
                    id: user.id,
                    email: user.email,
                    role: user.role,
                }))
                .sort((a, b) => a.email.localeCompare(b.email));
            setUserOptions(options);
        } else {
            showToast(error?.severity, error?.errorMsg || error?.summary);
        }
        setUserOptionsLoading(false);
    }, [headers, showToast]);

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    useEffect(() => {
        setCurrentPage(1);
        fetchAuditLogs(1);
    }, [fetchAuditLogs]);

    const dateRangeHandler = (e) => {
        setDates(e.value);
        setCurrentPage(1);
    };

    const handlePageChange = (page) => {
        if (page < 1) return;
        setCurrentPage(page);
        fetchAuditLogs(page);
    };

    const handleUsersChange = (e) => {
        const newValue = e.value || [];

        if (newValue.length > 15) {
            showToast("warn", "You can select a maximum of 15 items");
            return;
        }

        setSelectedUsers(newValue);
        setCurrentPage(1);
    };

    const handleSort = ({ sortOrder: nextSortOrder }) => {
        setCurrentPage(1);
        setSortOrder(nextSortOrder === 1 ? 1 : -1);
    };

    const handleActionChange = (e) => {
        setSelectedAction(e.value || null);
        setCurrentPage(1);
    };

    const descriptionTemplate = (rowData) => {
        const description = rowData.description;

        if (description && typeof description === "object" && description.target) {
            return (
                <div className="audit-description">
                    {description.prefix}{" "}
                    {description.wrapTarget ? "(" : ""}
                    {description.target?.responseID ? (
                        <Link className="audit-link" to={description.target.to} state={{ responseID: +description.target.responseID }}   onClick={() => localStorage.setItem('pageRefreshed', false)}
>
                            {description.target.label}
                        </Link>
                    ) : (
                         <Link className="audit-link" to={description.target.to}>
                            {description.target.label}
                        </Link>
                    )}
                    {description.wrapTarget ? ")" : ""}
                    {description.suffix}
                </div>
            );
        }

        return <div className="audit-description">{description && typeof description === "string" ? description : `${description?.prefix}${description?.suffix}`}</div>;
    };
    const createdAtTemplate = (rowData) => (
        <div>
            {rowData.createdAt && rowData.createdAt !== "0001-01-01T00:00:00Z" ? dateTimeFormat(rowData.createdAt) : "-"}
        </div>
    );
    const resetDateRangeHandler = () => {
        setDates(defaultDateRange);
        setCurrentPage(1);
    }

    return (
        <Layout >
            <section className="all-alerts-section audit-log-section">
                <div className="alerts-tab mt-3">
                    <div className="grid grid-nogutter">
                        <div className="col-12 lg:col-4 mb-2">
                            <div className="m-0">
                                <h1>Audit Log</h1>
                            </div>
                        </div>
                        <div className="col-12 lg:col-8 mb-2">
                            <div className="audit-log-filter">
                                <PR.MultiSelect
                                    inputId="auditLogUsers"
                                    value={selectedUsers}
                                    onChange={handleUsersChange}
                                    options={userOptions}
                                    optionLabel="email"
                                    optionValue="id"
                                    placeholder="Select Users"
                                    filter
                                    resetFilterOnHide
                                    showSelectAll={userOptions?.length <= 15}
                                    showClear
                                    maxSelectedLabels={0}
                                    selectedItemsLabel="{0} users selected"
                                    className="multi-select-dropdown"
                                    disabled={userOptionsLoading}
                                    panelClassName="audit-users-select"
                                    pt={{
                                        checkboxContainer: {
                                            onClick: (e) => {
                                                e.stopPropagation();
                                                const parent = e.target?.parentNode;
                                                if (parent && typeof parent.click === "function") {
                                                    parent.click();
                                                }
                                            },
                                        },
                                    }}
                                />
                                <PR.Dropdown
                                    value={selectedAction}
                                    onChange={handleActionChange}
                                    options={actionFilterOptions}
                                    optionLabel="label"
                                    placeholder="Select Action"
                                    showClear
                                    className="audit-action-dropdown"
                                />
                                <div className="custom-clear-calendar">
                                    <PR.Calendar
                                        value={dates}
                                        onChange={dateRangeHandler}
                                        iconPos="left"
                                        readOnlyInput
                                        showIcon
                                        selectionMode="range"
                                        className="audit-date-calendar"
                                        placeholder="Select Date"
                                        dateFormat="dd/mm/yy"
                                        maxDate={new Date()}
                                    />
                                    {!isDefaultDateRange && (
                                        <div className="custom-clear">
                                            <i className="pi pi-times text-sm" onClick={() => resetDateRangeHandler()}></i>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="card col-12 relative">
                            <PR.DataTable
                                value={auditLogs?.slice(0, pageCount)}
                                stripedRows
                                className="data-table audit-log-data-table"
                                loading={loading}
                                emptyMessage="No records found."
                                sortField="created_at"
                                sortOrder={sortOrder}
                                onSort={handleSort}
                            >
                                <PR.Column body={createdAtTemplate} header="Date & Time" sortable sortField="created_at" style={{maxwidth:"200px", minWidth:"180px"}}/>
                                <PR.Column field="email" header="Email" />
                                <PR.Column field="action" header="Action" />
                                <PR.Column field="ipAddress" header="IP Address" className="ip-address" />
                                <PR.Column field="statusCode" header="Status Code" style={{width:"12%"}}/>
                                <PR.Column body={descriptionTemplate} header="Description" style={{width:"40%"}}/>

                            </PR.DataTable>
                            {!!auditLogs?.length && (
                                <div className="pagination">
                                    <div className="grid grid-nogutter align-items-center justify-content-between">
                                        <span>Page {currentPage}</span>
                                        <ul className="flex list-none align-items-center gap-3 m-0 p-0">
                                            <li
                                                className={currentPage === 1 ? "disabled" : ""}
                                                onClick={() => handlePageChange(currentPage - 1)}
                                            >
                                                Previous
                                            </li>
                                            <li
                                                className={auditLogs?.length <= pageCount ? "disabled" : ""}
                                                onClick={() => handlePageChange(currentPage + 1)}
                                            >
                                                Next
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </section>
        </Layout>
    );
};

export default AuditLog;
